/*
    Copyright 2023 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { context, Exception, propagation, ROOT_CONTEXT, Span, SpanKind, SpanOptions, SpanStatusCode, trace, Tracer } from "@opentelemetry/api";
import type { Request, Response } from "express";

import { addErrorAttributes, setPropagatedResourceAttributes, SemConvTrace, getHttpHeadersToCapture, Attributes } from "@dynatrace/opentelemetry-core";

import { gcfDetector, cFunctionName } from "./Resources";

// ============================================================================

// TODO: should this start with 'dt.agent.nodejs.'?
const cInstrumentationLibraryName = "@dynatrace/opentelemetry-gcf";
const tracer: Tracer = trace.getTracer(cInstrumentationLibraryName);

let gcfResourceAttributes: Attributes;
let forceFlushCached = false;
let forceFlush: (() => Promise<void>) | undefined;

/**
 * Starts new server span and adds google cloud functions specific attributes.
 *
 * @param req the express request (see https://expressjs.com/en/api.html#req)
 * @returns a promise with the created span.
 */
export async function startHttpSpan(req: Request): Promise<Span> {
	const coldStart = isColdStart();
	if (coldStart) {
		gcfResourceAttributes = await gcfDetector.detect();
	}

	const parentContext = propagation.extract(ROOT_CONTEXT, req.headers);

	const reqAttributes = getHttpRequestSpanAttributes(req);
	const attrs: Attributes = {
		[SemConvTrace.FAAS_TRIGGER]: SemConvTrace.FaasTriggerValues.HTTP,
		[SemConvTrace.FAAS_COLDSTART]: coldStart,
		...gcfResourceAttributes,
		...reqAttributes
	};

	const options: SpanOptions = {
		attributes: attrs,
		kind: SpanKind.SERVER
	};

	const span = tracer.startSpan(cFunctionName, options, parentContext);
	setPropagatedResourceAttributes(span, gcfResourceAttributes);
	return span;
}

/**
 * Starts a new server span and calls the given function passing it the
 * created span as first argument.
 * Additionally the new span gets set in context and this context is activated
 * for the duration of the function call.
 *
 * @param req the express request (see https://expressjs.com/en/api.html#req)
 * @param fn function called in the context of the span and receives the newly created span as an argument
 */
export async function startActiveHttpSpan<F extends (span: Span) => ReturnType<F>>(req: Request,	fn: F): Promise<ReturnType<F> | undefined> {
	const span = await startHttpSpan(req);
	const ctxWithSpan = trace.setSpan(ROOT_CONTEXT, span);
	return context.with(ctxWithSpan, fn, undefined, span);
}

/**
 * Ends the given span and flushes the tracer provider.
 *
 * @param span the span to end.
 * @param res the express response (see  https://expressjs.com/en/api.html#res). Provided response should be fully prepared (e.g. status code set)
 * @param error an optional error that will be recorded before the span is ended.
 * @returns a promise to await the tracer provider's flush operation.
 */
export async function endHttpSpanAndFlush(span: Span, res: Response, error?: Exception): Promise<void> {
	endHttpSpan(span, res, error);

	return flushSpans();
}

/**
 * Ends the given span.
 *
 * @param span the span to end.
 * @param res the express response (see  https://expressjs.com/en/api.html#res). Provided response should be fully prepared (e.g. status code set)
 * @param error an optional error that will be recorded before the span is ended.
 */
export function endHttpSpan(span: Span, res: Response, error?: Exception): void {
	if (error != null) {
		addErrorAttributes(span, error);
	}

	// only set error status (see https://github.com/open-telemetry/opentelemetry-specification/blob/main/specification/trace/semantic_conventions/http.md#status)
	if (error != null || res.statusCode >= 500) {
		span.setStatus({ code: SpanStatusCode.ERROR });
	}

	span.setAttribute(SemConvTrace.HTTP_STATUS_CODE, res.statusCode);
	span.end();
}

/**
 * Flushes registered tracer provider. To successfully flush the registered tracer provider must have forceFlush method implemented.
 *
 * @returns flush promise
 */
export async function flushSpans(): Promise<void> {
	const flush = getForceFlush();
	if (typeof(flush) === "function") {
		return flush();
	}
}

/**
 * Extracts the relevant relevant incoming HTTP span attributes from the given express request object.
 *
 * @param req the express request (see https://expressjs.com/en/api.html#req)
 * @returns the span attributes for the incoming HTTP request.
 */
function getHttpRequestSpanAttributes(req: Request): Attributes {
	const attributes = captureHttpHeaders(req);
	attributes[SemConvTrace.HTTP_SCHEME] = req.protocol || "https";
	attributes[SemConvTrace.HTTP_METHOD] = req.method || "GET";
	attributes[SemConvTrace.HTTP_HOST] = req.hostname;
	attributes[SemConvTrace.HTTP_TARGET] = req.originalUrl;

	return attributes;
}

/**
 * Returns span attributes from captured http headers (e.g. referer, user-agent, etc.. )
 * @param req the express request (see https://expressjs.com/en/api.html#req)
 */
function captureHttpHeaders(req: Request): Attributes {
	const attrs: Attributes = {};
	const headersToCapture = getHttpHeadersToCapture();
	for (const [headerName, semConvKey] of Object.entries(headersToCapture)) {
		const headerValue = req.get(headerName);
		if (headerValue != null) {
			attrs[semConvKey] = headerValue;
		}
	}

	// TODO capture headers for agent config

	const forwarded = req.get("forwarded");
	if (forwarded != null) {
		attrs[SemConvTrace.DT_HTTP_REQUEST_HEADER_FORWARDED] = forwarded;
	} else {
		const xForwardedFor = req.get("x-forwarded-for");
		attrs[SemConvTrace.DT_HTTP_REQUEST_HEADER_X_FORWARDED_FOR] = xForwardedFor;
	}

	return attrs;
}

/**
 * Returns true if it is cold start, otherwise false
 * NOTE: the function is exported to make cold start unit testable
 */
export const isColdStart = function(): boolean {
	return gcfResourceAttributes == null;
};

/**
 * Tries to find forceFlush function in provided tracerProvider and returns it.
 * @returns forceFlush function if found, otherwise "<not found>"
 */
function getForceFlush(): (() => Promise<void>) | undefined {
	if (forceFlushCached) {
		return forceFlush;
	}

	forceFlushCached = true;

	const tracerProvider = trace.getTracerProvider();
	/* eslint-disable @typescript-eslint/no-unsafe-member-access */
	/* eslint-disable @typescript-eslint/no-unsafe-call */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let currentProvider: any = tracerProvider;

	if (typeof currentProvider.getDelegate === "function") {
		currentProvider = currentProvider.getDelegate();
	}
	/* eslint-disable @typescript-eslint/no-unsafe-member-access */

	if (typeof currentProvider.forceFlush === "function") {
		forceFlush = currentProvider.forceFlush.bind(currentProvider) as () => Promise<void>;
	}
	/* eslint-disable @typescript-eslint/no-unsafe-call */
	return forceFlush;
}

/**
 * Resets cached forceFlush function
 * NOTE: the function is exported only for unit testing
 */
export function resetForceFlushCache() {
	forceFlushCached = false;
	forceFlush = undefined;
}
